import React from 'react';
import {
  Group,
  Paper,
  ActionIcon,
  Select,
  Text,
  Title,
  Stack,
  Grid,
  Checkbox,
} from '@mantine/core';
import { IconX, IconGripVertical } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { useProposal } from '../../context/ProposalContext';
import { FEE_ACTIONS } from '../../reducers/feeReducer';
import { Currency, Fee, BaseFee, FeeOption } from '@/interfaces/fees';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import ExpandingTextArea from '@/components/ExpandingTextArea';

interface FeeSectionWrapperProps {
  title: string;
  index: number;
  children: React.ReactNode;
  fee: BaseFee;
}

export default function FeeSectionWrapper({ title, index, children, fee }: FeeSectionWrapperProps) {
  const { t } = useTranslation();
  const { dispatchProposal } = useProposal();
  const { setNodeRef, transform, transition, isDragging, listeners, attributes } = useSortable({
    id: fee.id,
  });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.8 : 1,
  };

  const defaults: Record<Exclude<FeeOption, 'Text'>, { intro: string; closing: string }> = {
    HourlyRate: {
      intro: t('hourlyRateIntro'),
      closing: t('hourlyRateClosing'),
    },
    LumpSum: {
      intro: t('lumpSumIntro'),
      closing: t('lumpSumClosing'),
    },
    Retainer: {
      intro: t('retainerIntro'),
      closing: t('retainerClosing'),
    },
  };

  const handleDelete = () => {
    dispatchProposal({
      type: FEE_ACTIONS.REMOVE_FEE,
      index,
    });
  };

  const handleCurrencyChange = (currency: Currency) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: { ...fee, currency },
    });
  };

  const handleIntroChange = (value: string) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: { ...fee, introParagraph: value },
    });
  };

  const handleClosingChange = (value: string) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: { ...fee, closingParagraph: value },
    });
  };

  const handleIntroToggle = (checked: boolean) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: {
        ...fee,
        introParagraph: checked
          ? defaults[fee.type as Exclude<FeeOption, 'Text'>].intro
          : undefined,
      },
    });
  };

  const handleClosingToggle = (checked: boolean) => {
    dispatchProposal({
      type: FEE_ACTIONS.UPDATE_FEE,
      index,
      payload: {
        ...fee,
        closingParagraph: checked
          ? defaults[fee.type as Exclude<FeeOption, 'Text'>].closing
          : undefined,
      },
    });
  };

  const currencies: Currency[] = ['USD', 'EUR', 'GBP', 'PLN'];

  return (
    <Paper withBorder radius="md" shadow="sm" p="md" ref={setNodeRef} style={style}>
      <Group justify="space-between" mb="md">
        <Group justify="space-between" w="40%">
          <Group>
            <div style={{ cursor: 'grab', padding: '0.5rem' }} {...listeners} {...attributes}>
              <IconGripVertical size={20} />
            </div>
            <Title order={4} fw={700}>
              {title}
            </Title>
          </Group>
          <Group>
            <Text>{t('currency')}: </Text>
            <Select
              data={currencies}
              value={fee.currency}
              onChange={(value) => handleCurrencyChange(value as Currency)}
              w={100}
            />
          </Group>
        </Group>
        <ActionIcon variant="subtle" color="red" onClick={handleDelete} aria-label={t('delete')}>
          <IconX size={20} />
        </ActionIcon>
      </Group>
      <Stack gap="md">
        <Checkbox
          label={t('addIntroduction')}
          checked={fee.introParagraph !== undefined}
          onChange={(e) => handleIntroToggle(e.currentTarget.checked)}
        />
        {fee.introParagraph !== undefined && fee.type !== 'Text' && (
          <ExpandingTextArea
            value={fee.introParagraph}
            onChange={(e) => handleIntroChange(e.target.value)}
            placeholder=""
            min_max_rows={2}
          />
        )}
        {children}
        <Checkbox
          label={t('addConclusion')}
          checked={fee.closingParagraph !== undefined}
          onChange={(e) => handleClosingToggle(e.currentTarget.checked)}
        />
        {fee.closingParagraph !== undefined && fee.type !== 'Text' && (
          <ExpandingTextArea
            value={fee.closingParagraph}
            onChange={(e) => handleClosingChange(e.target.value)}
            placeholder=""
            min_max_rows={2}
          />
        )}
      </Stack>
    </Paper>
  );
}
